import Collection from 'core/model/Collection';
import { computed } from 'mobx';
import $notifications from 'app/stores/notifications/$notifications';
import TenantModel from './TenantModel';

class TenantCollection extends Collection {
  get defaultSortState() {
    return {
      field: 'name',
      direction: 'asc'
    };
  }

  get url() {
    return '/api/ui/mkp-settings/tenant';
  }

  get model() {
    return TenantModel;
  }

  // Get list of tenants
  @computed
  get tenantAlertingOptions() {
    return this.models
      .map((tenant) => ({
        label: tenant.get('name'),
        value: `${tenant.id}`
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  @computed
  get tenantAlertingOptionsWithPolicies() {
    return this.models
      .map((tenant) => {
        const tenantId = `${tenant.id}`;
        const tenantSubpolicies = this.getSubpolicies([tenantId]) || [];
        const hasSubpolicies = tenantSubpolicies.length > 0;

        return {
          label: `${tenant.get('name')} ${hasSubpolicies ? '' : '(No assigned policies)'}`,
          value: tenantId,
          disabled: !hasSubpolicies
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  // Get map of subpolicies with their associated tenants
  @computed
  get subpolicyIdToTenant() {
    return this.models.reduce((tenantMap, tenant) => {
      tenant.subpolicies.forEach(({ id }) => {
        if (id) {
          tenantMap[id] = tenant;
        }
      });
      return tenantMap;
    }, {});
  }

  // Get map of policies with array of associated subpolicies & tenants
  @computed
  get tenantSubpoliciesByPolicyId() {
    return this.models.reduce((policyMap, tenant) => {
      tenant.subpolicies.forEach(({ id, parentId }) => {
        if (parentId && id) {
          const entry = { id, tenant };
          policyMap[parentId] = Array.isArray(policyMap[parentId]) ? [...policyMap[parentId], entry] : [entry];
        }
      });
      return policyMap;
    }, {});
  }

  // Get an array of subpolicies
  getSubpolicies(tenantIds) {
    return (
      this.models
        // optionally filter models to only the selected tenants
        .filter((model) => !tenantIds || tenantIds.length === 0 || tenantIds.includes(`${model.id}`))
        .flatMap((model) => model.subpolicies)
    );
  }

  // Get an array of subpolicy rule ids
  getSubpolicyRuleIds(tenantIds) {
    return (
      this.models
        // optionally filter models to only the selected tenants
        .filter((model) => !tenantIds || tenantIds.length === 0 || tenantIds.includes(`${model.id}`))
        .flatMap((model) => model.subpolicies.map((sp) => sp.activationSettings?.ruleId).filter(Boolean))
    );
  }

  async fetch(...args) {
    return $notifications.collection.fetch().then(() => super.fetch(...args));
  }
}

export default TenantCollection;
